import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../lib/cn';

export type PanelTone = 'default' | 'raised' | 'inset' | 'accent' | 'danger';

// `title` is widened to ReactNode for the header strip, so the native string-only
// `title` attribute is omitted from the base props.
export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Surface depth. `inset` reads as a recess, `raised` as a plate above the hull. */
  tone?: PanelTone;
  /** Heading shown in the panel's title strip. */
  title?: ReactNode;
  /** Small uppercase caption above the title — section or coordinate labels. */
  eyebrow?: ReactNode;
  /** Controls docked to the right of the title strip. */
  actions?: ReactNode;
  /** Draws the signature cyan corner brackets. */
  corners?: boolean;
  /** Cuts the top-left and bottom-right corners for a machined bevel. */
  cut?: boolean;
  /** Runs a slow scan line across the surface — marks a live/processing panel. */
  scanning?: boolean;
  /** Removes body padding, for panels wrapping tables or canvases. */
  flush?: boolean;
}

const TONES: Record<PanelTone, string> = {
  default: 'border-edge bg-plate shadow-panel',
  raised: 'border-edge-strong bg-plate-raised shadow-panel-lg',
  inset: 'border-edge-subtle bg-surface-base shadow-inset-edge',
  accent: 'border-silicate/35 bg-plate shadow-panel',
  danger: 'border-danger/45 bg-plate shadow-panel',
};

/**
 * The structural surface of the entire HUD.
 *
 * Everything that isn't the orbit canvas sits on a Panel: the queue, the event
 * feed, building lists, the sector map. Tone carries the depth hierarchy —
 * nesting `inset` inside `default` is the standard way to show a sub-region.
 */
export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  {
    tone = 'default',
    title,
    eyebrow,
    actions,
    corners = false,
    cut = false,
    scanning = false,
    flush = false,
    className,
    children,
    ...rest
  },
  ref,
) {
  const hasHeader = Boolean(title || eyebrow || actions);

  return (
    <div
      ref={ref}
      className={cn(
        'relative rounded-panel border',
        TONES[tone],
        corners && 'sg-corners',
        cut && 'sg-cut',
        className,
      )}
      {...rest}
    >
      {scanning ? <span className="sg-scanline rounded-panel" aria-hidden /> : null}

      {hasHeader ? (
        <div
          className={cn(
            'flex items-center justify-between gap-3 border-b border-edge-subtle px-3 py-2 sm:px-4 sm:py-2.5',
          )}
        >
          <div className="min-w-0">
            {eyebrow ? (
              <div className="truncate font-display text-label-sm uppercase text-content-muted">
                {eyebrow}
              </div>
            ) : null}
            {title ? (
              <h3 className="truncate font-display text-xs font-semibold uppercase tracking-[0.14em] text-content">
                {title}
              </h3>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-1.5">{actions}</div> : null}
        </div>
      ) : null}

      <div className={cn('relative', !flush && 'p-3 sm:p-4')}>{children}</div>
    </div>
  );
});

/** Divider matching the panel's border treatment. */
export function PanelDivider({ className, ...rest }: HTMLAttributes<HTMLHRElement>) {
  return (
    <hr className={cn('my-3 border-0 border-t border-edge-subtle', className)} {...rest} />
  );
}

/** Footer strip for panel-level actions. */
export function PanelFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 border-t border-edge-subtle px-3 py-2 sm:px-4 sm:py-2.5',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
